import React from "react";
import { useTranslation } from "react-i18next";
import {
  Box,
  IconButton,
  Link,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Tooltip,
  Typography,
} from "@mui/material";
import {
  PlayCircle as PlayIcon,
  Visibility as VisibilityIcon,
} from "@mui/icons-material";
import { EntityImage } from "../../components/EntityImage";
import type {
  ExplorerLivestreamCompetingCategory,
  ExplorerLivestreamCompetingChannel,
  ExplorerLivestreamCompetingWindows,
} from "../../services/api";
import { toChileanDateString } from "../../utils/dateUtils";
import { ensureDirectS3Url, getS3Url } from "../../utils/imageUtils";
import {
  competitionPageHref,
  overlappingCompetingChannels,
} from "./competingChannels";
import {
  livestreamRowIsLive,
  type LivestreamDetailListItem,
} from "./LivestreamDetailRecentList";
import {
  formatExplorerTableDateLine,
  formatExplorerTableTimeLine,
  formatLivestreamTableViewsCount,
  formatNumber,
  formatViewerIntegerFloor,
  LIVESTREAM_METRIC_LABELS,
} from "./utils";

const COMPETING_AVATAR_PX = 24;
const COMPETING_AVATAR_LIMIT = 4;

export type PeriodDayChartPoint = {
  incremental: number;
  cumulative: number;
};

export type PeriodDayLivePoint = {
  avgViewers: number;
  peakViewers: number;
};

export type LivestreamProgramPeriodDayDetailsProps = {
  date: string | null;
  views?: PeriodDayChartPoint;
  interactions?: PeriodDayChartPoint;
  live?: PeriodDayLivePoint;
  livestreams: LivestreamDetailListItem[];
  showProgramName?: boolean;
  competing?: ExplorerLivestreamCompetingWindows;
};

function chileNoonIso(date: string): string {
  return `${date}T15:00:00.000Z`;
}

function streamAirDate(item: LivestreamDetailListItem): string {
  return toChileanDateString(item.releaseTime || item.actualStartTime);
}

function streamDurationLabel(item: LivestreamDetailListItem): string {
  if (item.youtubeDuration != null && item.youtubeDuration > 0) {
    const hours = Math.floor(item.youtubeDuration / 3600);
    const mins = Math.floor((item.youtubeDuration % 3600) / 60);
    if (hours > 0) return `${hours}h ${mins}m`;
    return `${mins}m`;
  }
  if (!item.actualStartTime || !item.actualEndTime) return "—";
  const start = new Date(item.actualStartTime);
  const end = new Date(item.actualEndTime);
  const diffMs = end.getTime() - start.getTime();
  if (!Number.isFinite(diffMs) || diffMs < 0) return "—";
  const hours = Math.floor(diffMs / (1000 * 60 * 60));
  const mins = Math.floor((diffMs % (1000 * 60 * 60)) / (1000 * 60));
  if (hours > 0) return `${hours}h ${mins}m`;
  return `${mins}m`;
}

const Metric: React.FC<{ label: string; value: string }> = ({
  label,
  value,
}) => (
  <Paper
    elevation={0}
    sx={{
      p: 1.5,
      bgcolor: "background.paper",
      border: "1px solid",
      borderColor: "divider",
      minWidth: 0,
    }}
  >
    <Typography variant="caption" color="text.secondary" display="block">
      {label}
    </Typography>
    <Typography variant="body1" fontWeight={700}>
      {value}
    </Typography>
  </Paper>
);

export const LivestreamProgramPeriodDayDetails: React.FC<
  LivestreamProgramPeriodDayDetailsProps
> = ({
  date,
  views,
  interactions,
  live,
  livestreams,
  showProgramName = false,
  competing,
}) => {
  const { t } = useTranslation("explorer");
  const dayStreams = React.useMemo(() => {
    if (!date) return [];
    return livestreams
      .filter((item) => streamAirDate(item) === date)
      .sort((left, right) => {
        const liveCmp =
          Number(livestreamRowIsLive(right)) - Number(livestreamRowIsLive(left));
        if (liveCmp !== 0) return liveCmp;
        const leftTime = left.releaseTime || left.actualStartTime || "";
        const rightTime = right.releaseTime || right.actualStartTime || "";
        return leftTime.localeCompare(rightTime);
      });
  }, [date, livestreams]);

  if (!date) {
    return (
      <Typography variant="body2" color="text.secondary">
        {t("clickDayForDetails")}
      </Typography>
    );
  }

  return (
    <Box>
      <Typography variant="subtitle1" fontWeight={700} sx={{ mb: 1.5 }}>
        {formatExplorerTableDateLine(chileNoonIso(date))}
      </Typography>
      <Box
        sx={{
          display: "grid",
          gridTemplateColumns: "repeat(auto-fit, minmax(140px, 1fr))",
          gap: 1.5,
          mb: 3,
        }}
      >
        {views ? (
          <>
            <Metric
              label={t("programMetricDayViewsGained")}
              value={formatNumber(views.incremental)}
            />
            <Metric
              label={t("programMetricDayViewsTotal")}
              value={formatNumber(views.cumulative)}
            />
          </>
        ) : null}
        {interactions ? (
          <Metric
            label={t("programMetricDayInteractionsGained")}
            value={formatNumber(interactions.incremental)}
          />
        ) : null}
        {live ? (
          <>
            <Metric
              label={LIVESTREAM_METRIC_LABELS.avgEsp}
              value={formatViewerIntegerFloor(live.avgViewers)}
            />
            <Metric
              label="Peak Max"
              value={formatViewerIntegerFloor(live.peakViewers)}
            />
          </>
        ) : null}
      </Box>

      <Typography variant="body2" fontWeight={600} sx={{ mb: 1.25 }}>
        {t("livestreams")}
      </Typography>
      {dayStreams.length === 0 ? (
        <Typography variant="body2" color="text.secondary">
          {t("programMetricDayNoLivestreams")}
        </Typography>
      ) : (
        <Table size="small" sx={{ width: "100%" }}>
          <TableHead>
            <TableRow>
              <TableCell sx={{ width: "1%", whiteSpace: "nowrap" }}>
                {t("time")}
              </TableCell>
              <TableCell sx={{ width: "99%" }}>{t("title")}</TableCell>
              <TableCell align="right">{t("views")}</TableCell>
              <TableCell align="right">
                {LIVESTREAM_METRIC_LABELS.avgEsp}
              </TableCell>
              <TableCell align="right">Peak Max</TableCell>
              <TableCell align="right">{t("duration")}</TableCell>
              <TableCell sx={{ width: "1%", whiteSpace: "nowrap" }}>
                {t("competingWith")}
              </TableCell>
              <TableCell sx={{ width: 40, px: 0.5 }} />
            </TableRow>
          </TableHead>
          <TableBody>
            {dayStreams.map((item) => {
              const youtubeUrl = item.url?.trim() || undefined;
              const titleText = item.title?.trim() || t("noTitle");
              const start = item.releaseTime || item.actualStartTime;
              return (
                <TableRow key={item._id}>
                  <TableCell sx={{ width: "1%", whiteSpace: "nowrap" }}>
                    {formatExplorerTableTimeLine(start)}
                  </TableCell>
                  <TableCell sx={{ width: "99%" }}>
                    <Typography variant="body2" fontWeight={600} noWrap>
                      {titleText}
                    </Typography>
                    {showProgramName ? (
                      <Typography variant="caption" color="text.secondary">
                        {item.livestreamProgramId?.trim()
                          ? item.programName?.trim() || t("program")
                          : t("noProgram")}
                      </Typography>
                    ) : null}
                  </TableCell>
                  <TableCell align="right">
                    {formatLivestreamTableViewsCount(item.totalViews)}
                  </TableCell>
                  <TableCell align="right">
                    {formatViewerIntegerFloor(item.averageViewers || 0)}
                  </TableCell>
                  <TableCell align="right">
                    {formatViewerIntegerFloor(item.peakViewers || 0)}
                  </TableCell>
                  <TableCell align="right">
                    {streamDurationLabel(item)}
                  </TableCell>
                  <TableCell sx={{ width: "1%", whiteSpace: "nowrap" }}>
                    <CompetingChannelLogos
                      stream={item}
                      category={competing?.category}
                      channels={overlappingCompetingChannels(item, competing)}
                    />
                  </TableCell>
                  <TableCell sx={{ width: 40, px: 0.5 }}>
                    {youtubeUrl ? (
                      <Link href={youtubeUrl} target="_blank" rel="noopener noreferrer">
                        <IconButton
                          size="small"
                          sx={{ color: "error.main" }}
                          aria-label={t("openOnYouTube")}
                        >
                          <PlayIcon />
                        </IconButton>
                      </Link>
                    ) : null}
                  </TableCell>
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      )}
    </Box>
  );
};

function CompetingChannelLogos({
  stream,
  category,
  channels,
}: {
  stream: LivestreamDetailListItem;
  category?: ExplorerLivestreamCompetingCategory;
  channels: ExplorerLivestreamCompetingChannel[];
}) {
  const { t } = useTranslation("explorer");
  const competitionHref = competitionPageHref(stream, category ?? "tv");

  if (channels.length === 0) {
    return (
      <Typography variant="body2" color="text.secondary">
        —
      </Typography>
    );
  }

  const shown = channels.slice(0, COMPETING_AVATAR_LIMIT);
  const extra = channels.length - shown.length;

  return (
    <Stack
      direction="row"
      alignItems="center"
      justifyContent="flex-start"
    >
      {shown.map((channel, index) => {
        const src = channel.imageS3Key
          ? ensureDirectS3Url(getS3Url(channel.imageS3Key))
          : undefined;
        return (
          <Tooltip key={channel.name} title={channel.name}>
            <Box
              sx={{
                display: "inline-flex",
                ml: index === 0 ? 0 : -0.85,
                zIndex: shown.length - index,
              }}
            >
              <EntityImage
                name={channel.name}
                src={src}
                size={COMPETING_AVATAR_PX}
                variant="plain"
                sx={{
                  border: "2px solid",
                  borderColor: "background.default",
                  boxSizing: "content-box",
                }}
              />
            </Box>
          </Tooltip>
        );
      })}
      {extra > 0 ? (
        <Typography
          variant="caption"
          color="text.secondary"
          sx={{ ml: 0.5, flexShrink: 0 }}
        >
          +{extra}
        </Typography>
      ) : null}
      {competitionHref ? (
        <Tooltip title={t("seeCompetition")}>
          <IconButton
            size="small"
            component="a"
            href={competitionHref}
            target="_blank"
            rel="noopener noreferrer"
            aria-label={t("seeCompetition")}
            sx={{ ml: 0.25 }}
          >
            <VisibilityIcon sx={{ fontSize: 16 }} />
          </IconButton>
        </Tooltip>
      ) : null}
    </Stack>
  );
}
